/**
 * SportsHome — /sports
 *
 * Landing page for Social Pulse Sports. Shows the hero pitch,
 * quick actions (create tournament / register club), popular
 * sports, upcoming tournaments and featured clubs.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Trophy, Users, Calendar, MapPin, Sparkles, ArrowRight, PlusCircle, Video,
} from "lucide-react";
import { http } from "@/lib/api";
import SportsCategoryNav from "@/components/SportsCategoryNav";
import TopicNewsStrip from "@/components/TopicNewsStrip";
import { useLocality } from "@/contexts/LocalityContext";

export default function SportsHome() {
  const [data, setData] = useState({ upcoming_tournaments: [], featured_clubs: [], popular_sports: [] });
  const [meta, setMeta] = useState(null);
  const [local, setLocal] = useState({ clubs: [], tournaments: [] });
  const { locality } = useLocality();

  useEffect(() => {
    (async () => {
      const [h, m] = await Promise.all([
        http.get("/sports/home").then(r => r.data).catch(() => ({})),
        http.get("/sports/meta").then(r => r.data).catch(() => null),
      ]);
      setData({ upcoming_tournaments: [], featured_clubs: [], popular_sports: [], ...h });
      setMeta(m);
    })();
  }, []);

  // Location-aware local content: refetches when locality changes.
  useEffect(() => {
    if (!locality?.country && !locality?.city) { setLocal({ clubs: [], tournaments: [] }); return; }
    const params = {};
    if (locality?.country) params.country = locality.country;
    if (locality?.city) params.city = locality.city;
    params.limit = 6;
    (async () => {
      const [c, t] = await Promise.all([
        http.get("/sports/clubs",       { params }).then(r => r.data?.items || []).catch(() => []),
        http.get("/sports/tournaments", { params }).then(r => r.data?.items || []).catch(() => []),
      ]);
      setLocal({ clubs: c, tournaments: t });
    })();
  }, [locality?.country, locality?.state, locality?.city]);

  return (
    <>
      <SportsCategoryNav />
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="sports-home">

        {/* Latest Sports news */}
        <TopicNewsStrip topic="Sports" category="Sports" testId="sports-news-strip" />

        {/* Hero */}
        <section className="rounded-xl p-6 md:p-10 mb-8" style={{ background: "var(--wofy-surface)" }}>
          <div className="overline mb-2">SOCIAL PULSE SPORTS</div>
          <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-3">
            Play. Compete. Connect. Celebrate.
          </h1>
          <p className="max-w-2xl opacity-80">
            From neighborhood cricket matches and school football leagues to marathons,
            pickleball tournaments, and traditional village games — Social Pulse Sports
            brings athletes, teams, clubs, and communities together.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/sports/tournaments/new" className="wofy-btn-primary inline-flex items-center gap-1.5" data-testid="cta-create-tournament">
              <PlusCircle className="w-4 h-4" /> Create a tournament
            </Link>
            <Link to="/sports/clubs/new" className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border hover:bg-black/5" style={{ borderColor: "var(--wofy-border)" }} data-testid="cta-register-club">
              <Users className="w-4 h-4" /> Register your club
            </Link>
            <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full" style={{ background: "#DCFCE7", color: "#166534" }}>
              <Video className="w-3.5 h-3.5" /> Now with YouTube &amp; Vimeo embeds on every page
            </span>
          </div>
        </section>

        {/* Near You — location-scoped strip */}
        {(locality?.city || locality?.country) && (
          <section className="mb-10" data-testid="sports-near-you">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div>
                <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>
                  <MapPin className="w-3 h-3 inline mr-1 -mt-0.5" />
                  SPORTS NEAR YOU · {[locality.city, locality.state, locality.country].filter(Boolean).join(", ").toUpperCase()}
                </div>
                <h2 className="font-display text-2xl">Right in your area</h2>
              </div>
              <Link
                to={`/sports/clubs?${locality?.country ? `country=${encodeURIComponent(locality.country)}` : ""}${locality?.city ? `&city=${encodeURIComponent(locality.city)}` : ""}`}
                className="text-sm wofy-link inline-flex items-center gap-0.5"
              >
                See all local <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            {local.clubs.length === 0 && local.tournaments.length === 0 ? (
              <div className="wofy-card !p-6 text-center opacity-80">
                <p className="mb-3">No clubs or tournaments listed for your area yet.</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  <Link to="/sports/tournaments/new" className="wofy-btn-primary inline-flex items-center gap-1.5 text-sm">
                    <PlusCircle className="w-4 h-4" /> Create a tournament
                  </Link>
                  <Link to="/sports/clubs/new" className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border hover:bg-black/5" style={{ borderColor: "var(--wofy-border)" }}>
                    <Users className="w-4 h-4" /> Register your club
                  </Link>
                </div>
              </div>
            ) : (
              <>
                {local.tournaments.length > 0 && (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-4">
                    {local.tournaments.slice(0, 3).map(t => <TournamentCard key={t.id} t={t} />)}
                  </div>
                )}
                {local.clubs.length > 0 && (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {local.clubs.slice(0, 3).map(c => <ClubCard key={c.id} c={c} />)}
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {/* Popular sports */}
        <section className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-2xl">Popular sports</h2>
            <Link to="/sports/categories" className="text-sm wofy-link inline-flex items-center gap-0.5">
              All categories <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="flex flex-wrap gap-2">
            {(data.popular_sports.length > 0
              ? data.popular_sports.map(s => ({ slug: s.sport, label: s.label || s.sport, count: s.count }))
              : (meta?.categories || []).slice(0, 3).flatMap(c => c.sports.slice(0, 5)).map(s => ({ ...s, count: 0 }))
            ).map(s => (
              <Link key={s.slug} to={`/sports/tournaments?sport=${s.slug}`}
                className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border hover:bg-black/5 transition"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid={`popular-sport-${s.slug}`}>
                <Trophy className="w-3.5 h-3.5" />{s.label}
                {s.count > 0 && <span className="text-[10px] opacity-60">· {s.count}</span>}
              </Link>
            ))}
          </div>
        </section>

        {/* Upcoming tournaments */}
        <section className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-2xl">Upcoming tournaments</h2>
            <Link to="/sports/tournaments" className="text-sm wofy-link inline-flex items-center gap-0.5">
              Browse all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {data.upcoming_tournaments.length === 0 ? (
            <div className="wofy-card !p-6 text-center opacity-70">
              No tournaments listed yet. <Link to="/sports/tournaments/new" className="wofy-link">Be the first to add one →</Link>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {data.upcoming_tournaments.map(t => <TournamentCard key={t.id} t={t} />)}
            </div>
          )}
        </section>

        {/* Featured clubs */}
        <section className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-2xl">Featured clubs &amp; teams</h2>
            <Link to="/sports/clubs" className="text-sm wofy-link inline-flex items-center gap-0.5">
              All clubs <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {data.featured_clubs.length === 0 ? (
            <div className="wofy-card !p-6 text-center opacity-70">
              No clubs yet. <Link to="/sports/clubs/new" className="wofy-link">Register your club →</Link>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {data.featured_clubs.map(c => <ClubCard key={c.id} c={c} />)}
            </div>
          )}
        </section>

        {/* Feature grid */}
        <section className="mb-4">
          <h2 className="font-display text-2xl mb-3">What you can do here</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: Trophy, title: "Host tournaments", body: "Cricket cups, school championships, charity 5Ks — publish schedules, rules and results." },
              { icon: Users,  title: "Recruit players",  body: "Post open spots, tryouts, coaching wants. Applicants apply with one click." },
              { icon: Calendar, title: "Run leagues",    body: "Season fixtures, standings, top scorers and MVP awards, all in one place." },
              { icon: Video,  title: "Share the moments", body: "Embed YouTube or Vimeo highlights right on your team, tournament and venue pages." },
              { icon: MapPin, title: "Find sports near you", body: "Filter by city, state or country — from village kabaddi to city marathons." },
              { icon: Sparkles, title: "AI sports assistant (coming)", body: "Auto-generate schedules, fixture posters, participant reminders and certificates." },
            ].map(({ icon: Icon, title, body }, i) => (
              <div key={i} className="wofy-card !p-4">
                <div className="w-9 h-9 rounded-lg bg-black/5 grid place-items-center mb-2"><Icon className="w-5 h-5" /></div>
                <div className="font-medium">{title}</div>
                <p className="text-sm opacity-80 mt-1">{body}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}

function TournamentCard({ t }) {
  return (
    <Link to={`/sports/tournaments/${t.slug}`}
      className="wofy-card !p-4 hover:shadow-md transition block"
      data-testid={`tournament-card-${t.slug}`}>
      {t.cover_image_url && (
        <div className="w-full h-32 rounded-lg overflow-hidden mb-3 bg-black/5"
             style={{ backgroundImage: `url(${t.cover_image_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />
      )}
      <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-1">
        <Trophy className="w-3 h-3 inline mr-1" />{t.sport}{t.format ? ` · ${t.format}` : ""}
      </div>
      <div className="font-medium mb-1 line-clamp-2">{t.name}</div>
      <div className="text-xs opacity-70 flex items-center gap-1">
        <Calendar className="w-3 h-3" />{t.start_date || "TBA"}{t.end_date && t.end_date !== t.start_date ? ` – ${t.end_date}` : ""}
      </div>
      {(t.city || t.country) && (
        <div className="text-xs opacity-70 flex items-center gap-1 mt-0.5">
          <MapPin className="w-3 h-3" />{[t.city, t.state, t.country].filter(Boolean).join(", ")}
        </div>
      )}
    </Link>
  );
}

function ClubCard({ c }) {
  return (
    <Link to={`/sports/clubs/${c.slug}`}
      className="wofy-card !p-4 hover:shadow-md transition block"
      data-testid={`club-card-${c.slug}`}>
      <div className="flex items-center gap-3 mb-2">
        {c.logo_url ? (
          <img src={c.logo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-black/5 grid place-items-center text-xs font-mono">
            {(c.name || "?")[0].toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          <div className="font-medium truncate">{c.name}</div>
          <div className="text-[10px] font-mono uppercase tracking-wider opacity-60">{c.sport}</div>
        </div>
      </div>
      {c.description && <p className="text-sm opacity-80 line-clamp-2">{c.description}</p>}
      {(c.city || c.country) && (
        <div className="text-xs opacity-70 flex items-center gap-1 mt-2">
          <MapPin className="w-3 h-3" />{[c.city, c.state, c.country].filter(Boolean).join(", ")}
        </div>
      )}
    </Link>
  );
}
